import React, { useState, useEffect, useCallback } from 'react';
import { TKiroDetail, useMeds } from '../../context/MedDirProvider';
import Modal from '../modal/Modal';
import Button from '../shared/ui/Button/baseBtn';
import CheckboxInput from '../shared/ui/input/CheckBoxInput';
import SelectInput from '../shared/ui/input/SelectInput';
import Input from '../shared/ui/input/Input';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../shared/ui/Spinner';

export interface UpdateDrugModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface ActiveIngredient {
    id: string;
    name_en: string;
    name_ru: string;
    his_id: string;
}

interface DosageUnit {
    id: string;
    name_ru: string;
}

type DrugFormData = {
    use_in_kiro: boolean;
    active_ingredient: string | null;
    concentration: string | null;
    concentration_unit: string | null;
    vial_dose: string | null;
    vial_unit: string | null;
    density: string | null;
    final_container_id: number | null;
    cycle_type: string | null;
}

const cycleTypes = [
    { id: "1", label: "Bag with diluent" },
    { id: "2", label: "Syringe" },
    { id: "3", label: "Empty bag + drug" },
    { id: "4", label: "Infuser (diluent + drug)" },
    { id: "5", label: "Cassette (diluent + drug)" },
    { id: "6", label: "Empty bag (diluent + drug)" },
    { id: "7", label: "Infuser + drug" },
    { id: "8", label: "Cassette + drug" },
];


const UpdateDrugModal: React.FC<UpdateDrugModalProps> = ({
    isOpen,
    onClose,
}) => {
    const [formData, setFormData] = useState<DrugFormData>({} as DrugFormData);
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [activeIngredients, setActiveIngredients] = useState<ActiveIngredient[]>([]);
    const [dosageUnits, setDosageUnits] = useState<DosageUnit[]>([]);
    const [loadingIngredients, setLoadingIngredients] = useState(false);
    const [loadingUnits, setLoadingUnits] = useState(false);
    const { t } = useTranslation()
    const {
        getActiveIngridients,
        getDosageUnits,
        drugDetail,
        alertMsgs,
        editDrug,
        setDrugDetail,
        setAlertMessages
    } = useMeds();
    useEffect(() => {
        if (isOpen && !loadingUnits && drugDetail) {
            const ingredient = activeIngredients.find((elm) =>
                elm.name_ru === drugDetail.kiro_details.active_ingredient ||
                elm.name_en === drugDetail.kiro_details.active_ingredient ||
                elm.his_id === drugDetail.kiro_details.active_ingredient
            );
            const concentration = dosageUnits.find((elm) =>
                elm.name_ru === drugDetail.kiro_details.concentration_unit
            )
            const vialUnit = dosageUnits.find((elm) =>
                elm.name_ru === drugDetail.kiro_details.vial_unit
            )
            setFormData({
                use_in_kiro: drugDetail.use_in_kiro || false,
                active_ingredient: ingredient?.id ? String(ingredient.id) : null,
                concentration: drugDetail.kiro_details.concentration || null,
                concentration_unit: concentration?.id ? String(concentration.id) : null,
                vial_dose: drugDetail.kiro_details.vial_dose || null,
                vial_unit: vialUnit?.id ? String(vialUnit.id) : null,
                density: drugDetail.kiro_details.density || null,
                final_container_id: drugDetail.kiro_details.final_container_id || null,
                cycle_type: drugDetail.kiro_details.cycle_type || null
            });

            setSubmitted(false);
        }
    }, [isOpen, drugDetail,loadingUnits]);

    useEffect(() => {
        if (!isOpen) return;
        const fetchData = async () => {
            setLoadingIngredients(true);
            setLoadingUnits(true);
            try {
                const [ingredientsRes, unitsRes] = await Promise.all([
                    getActiveIngridients(),
                    getDosageUnits(),
                ]);
                setActiveIngredients(ingredientsRes.results || []);
                setDosageUnits(unitsRes.results || []);
            } catch (e) {
                console.error('Error fetching data:', e);
            } finally {
                setLoadingIngredients(false);
                setLoadingUnits(false);
            }
        };

        fetchData();
    }, [isOpen]);

    const handleInputChange = useCallback(
        (field: keyof DrugFormData, value: any) => {
            setFormData((prev) => ({ ...prev, [field]: value }));
        },
        [submitted]
    );

    const handleSave = async () => {
        setSubmitted(true);
        setIsLoading(true);

        if (!drugDetail) {
            setIsLoading(false);
            return;
        }

        const payload: TKiroDetail = {
            use_in_kiro: formData.use_in_kiro,
            active_ingredient: formData.active_ingredient ?? null,
            concentration: formData.concentration ?? null,
            concentration_unit: formData.concentration_unit ?? null,
            vial_dose: formData.vial_dose ?? null,
            vial_unit: formData.vial_unit ?? null,
            density: formData.density ?? null,
            final_container_id: formData.final_container_id ?? null,
            cycle_type: formData.cycle_type ?? null,
        };

        try {
            const response = await editDrug(drugDetail.id, payload);
            if (response) {
                setDrugDetail(response);
                setIsLoading(false);
                onClose();
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
        }
    };


    const handleCancel = () => {
        setFormData({} as DrugFormData);
        setSubmitted(false);
        onClose();
        setAlertMessages({})
    };
    const i18nMedDirectory = (key: string): string =>
        t(`med-directory.${key}`);
    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            className="flex flex-col"
            title={i18nMedDirectory("edit-medication")}
            size="xl"
            headerClass="bg-gray-50 pb-4 text-gray-900 text-lg font-normal"
        >
            {
                loadingUnits ?
                    <div className="flex-1 h-screen flex items-center justify-center">
                        <LoadingSpinner size="lg" />
                    </div >
                    :
                    <>
                        <div className="flex-1 overflow-y-auto bg-white">
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSave();
                                }}
                            >
                                <div className="px-6">
                                    <div className="space-y-2 border-b border-background-light-gray p-4">
                                        <h3 className="text-md font-semibold text-gray-900">{i18nMedDirectory('general')}</h3>
                                        <p className="text-sm text-gray-600">
                                            {i18nMedDirectory('general-desc')}
                                        </p>
                                    </div>
                                    <div className="p-4 grid grid-cols-[12rem_1fr] items-center border-b border-background-light-gray text-sm text-gray-900 gap-8">
                                        <p className="font-medium">{i18nMedDirectory("kiro")}</p>
                                        <CheckboxInput
                                            className="flex items-center"
                                            label={i18nMedDirectory("use-in-kirolink")}
                                            checked={formData.use_in_kiro}
                                            onChange={(e) => handleInputChange('use_in_kiro', e.target.checked)}
                                        />
                                    </div>
                                </div>

                                <div className="px-6">
                                    <div className="space-y-2 border-b border-background-light-gray p-4">
                                        <h3 className="text-md font-semibold text-gray-900">{i18nMedDirectory('ingridients')}</h3>
                                        <p className="text-sm text-gray-600">
                                            {i18nMedDirectory('ingridients-desc')}
                                        </p>
                                    </div>
                                    <div className="p-4 grid grid-cols-[12rem_1fr] items-center border-b border-background-light-gray text-sm text-gray-900 gap-8">
                                        <p className="font-medium">{i18nMedDirectory("active-ingridients")}</p>
                                        <SelectInput
                                            size='lg'
                                            clearable
                                            value={formData.active_ingredient}
                                            onChange={(e) => handleInputChange('active_ingredient', e)}
                                            onClear={() => handleInputChange('active_ingredient', '')}
                                            searchable
                                            options={activeIngredients.map(ingredient => ({
                                                id: ingredient.id,
                                                label: ingredient.name_ru || ingredient.name_en || ingredient.his_id,
                                            }))}
                                            error={alertMsgs['active_ingredient'] ? alertMsgs['active_ingredient'][0] : ''}
                                            required
                                            important
                                            disabled={loadingIngredients}
                                            noResultsText={i18nMedDirectory('no-ingredients')}
                                        />
                                    </div>
                                </div>

                                <div className="px-6">
                                    <div className="space-y-2 border-b border-background-light-gray p-4">
                                        <h3 className="text-md font-semibold text-gray-900">{i18nMedDirectory('dosage-admin')}</h3>
                                        <p className="text-sm text-gray-600">
                                            {i18nMedDirectory('dosage-admin-desc')}
                                        </p>
                                    </div>

                                    <div className="p-4 grid grid-cols-[12rem_1fr] border-b border-background-light-gray text-sm text-gray-900 gap-8">
                                        <p className="font-medium">{i18nMedDirectory('concentration')}</p>
                                        <div className="flex gap-5">
                                            <Input
                                                label={i18nMedDirectory('concentration')}
                                                className="max-w-[250px]"
                                                value={formData.concentration || ''}
                                                onChange={(e) => handleInputChange('concentration', e.target.value)}
                                                placeholder="0.0000"
                                                error={alertMsgs['concentration'] ? alertMsgs['concentration'][0] : ''}

                                            />
                                            <SelectInput
                                                label={i18nMedDirectory('concentration-unit')}
                                                clearable
                                                options={dosageUnits.map((u) => ({
                                                    id: u.id,
                                                    label: u.name_ru,
                                                    name: u.name_ru,
                                                }))}
                                                className="max-w-[250px]"
                                                value={formData.concentration_unit}
                                                onChange={(value: any) => handleInputChange('concentration_unit', value)}
                                                onClear={() => handleInputChange('concentration_unit', '')}
                                                searchable
                                                disabled={loadingUnits}
                                                noResultsText={i18nMedDirectory('no-units')}
                                                error={alertMsgs['concentration_unit'] ? alertMsgs['concentration_unit'][0] : ''}

                                            />
                                        </div>
                                    </div>

                                    <div className="p-4 grid grid-cols-[12rem_1fr] border-b border-background-light-gray text-sm text-gray-900 gap-8">
                                        <p className="font-medium">{i18nMedDirectory('vial-dose')}</p>
                                        <div className="flex gap-5">
                                            <Input
                                                label={i18nMedDirectory('vial-dose')}
                                                className="max-w-[250px]"
                                                value={formData.vial_dose || ''}
                                                onChange={(e) => handleInputChange('vial_dose', e.target.value)}
                                                placeholder="0.0000"
                                                error={alertMsgs['vial_dose'] ? alertMsgs['vial_dose'][0] : ''}

                                            />
                                            <SelectInput
                                                label={i18nMedDirectory('vial-dose-unit')}
                                                clearable
                                                options={dosageUnits.map((u) => ({
                                                    id: u.id,
                                                    label: u.name_ru,
                                                    name: u.name_ru,
                                                }))}
                                                className="max-w-[250px]"
                                                value={formData.vial_unit}
                                                onChange={(value: any) => handleInputChange('vial_unit', value)}
                                                onClear={() => handleInputChange('vial_unit', '')}
                                                searchable
                                                disabled={loadingUnits}
                                                noResultsText={i18nMedDirectory('no-units')}
                                                error={alertMsgs['vial_unit'] ? alertMsgs['vial_unit'][0] : ''}
                                            />
                                        </div>
                                    </div>

                                    <div className="p-4 grid grid-cols-[12rem_1fr] border-b border-background-light-gray text-sm text-gray-900 gap-8">
                                        <p className="font-medium">{i18nMedDirectory('density')} (mg/ml)</p>
                                        <Input
                                            type='number'
                                            className="max-w-[520px]"
                                            value={formData.density || ''}
                                            onChange={(e) => handleInputChange('density', e.target.value)}
                                            placeholder="0.0000"
                                            error={alertMsgs['density'] ? alertMsgs['density'][0] : ''}

                                        />
                                    </div>
                                    <div className="p-4 grid grid-cols-[12rem_1fr] border-b border-background-light-gray text-sm text-gray-900 gap-8">
                                        <p className="font-medium">{i18nMedDirectory('final_container_id')}</p>
                                        <Input
                                            type='number'
                                            className="max-w-[520px]"
                                            value={formData.final_container_id || ''}
                                            onChange={(e) => handleInputChange('final_container_id', e.target.value || null)}
                                            error={alertMsgs['final_container_id'] ? alertMsgs['final_container_id'][0] : ''}

                                        />
                                    </div>
                                    <div className="p-4 grid grid-cols-[12rem_1fr] text-sm text-gray-900 gap-8">
                                        <p className="font-medium">{i18nMedDirectory('cycle-type')}</p>
                                        <SelectInput
                                            clearable
                                            required={!!formData.final_container_id}
                                            options={cycleTypes}
                                            className="max-w-[520px]"
                                            value={formData.cycle_type || ""}
                                            onChange={(value: any) => handleInputChange('cycle_type', value)}
                                            onClear={() => handleInputChange('cycle_type', null)}
                                            noResultsText={i18nMedDirectory('no-units')}
                                            error={alertMsgs['cycle_type'] ? alertMsgs['cycle_type'][0] : ''}
                                        />
                                    </div>
                                    <div className="h-4"></div>
                                </div>
                            </form>
                        </div>

                        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 sticky bottom-0 z-10">
                            <Button size="sm" variant="outline" onClick={handleCancel} disabled={isLoading}>
                                {i18nMedDirectory('cancel')}
                            </Button>
                            <Button size="sm" onClick={handleSave} disabled={isLoading}>
                                {isLoading ? i18nMedDirectory('saving') : `${i18nMedDirectory('save')}`}
                            </Button>
                        </div>
                    </>
            }
        </Modal>
    );
};

export default UpdateDrugModal;